import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import { getDataWithToken } from "../../services/GetDataService";
import { useSnackbar } from "notistack";
import PulseLoader from "react-spinners/PulseLoader";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";

const customeTextFeild = {
  boxShadow: "0px 1px 2px 0px rgba(15, 22, 36, 0.05)",
  "& .MuiInput-underline:after": {
    borderBottomColor: "#B2BAC2",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "",
    },
    "&:hover fieldset": {
      borderColor: "#969696",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#969696",
    },
  },
};

const SearchCouponDialog = ({ totalAmount, set_discount_amount }) => {
  const { logout } = useContext(AuthContext);
  const [couponDialog, setCouponDialog] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleSnakbarOpen = (msg, vrnt) => {
    enqueueSnackbar(msg, {
      variant: vrnt,
      autoHideDuration: vrnt === "error" ? 3000 : 1000,
    });
  };

  const resetDialogState = () => {
    setCouponCode("");
    setCouponResult(null);
  };

  const handleCouponDialogClose = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      setCouponDialog(false);
      resetDialogState();
    }
  };

  const calculateCouponDiscount = (coupon) => {
    if (!coupon) return 0;
    if (coupon.discount_type === "percentage") {
      return Math.round(
        (totalAmount * Number(coupon.discount_value || 0)) / 100
      );
    }
    return Number(coupon.discount_value || 0);
  };

  const searchCoupon = async () => {
    if (!couponCode.trim()) {
      handleSnakbarOpen("Please enter coupon code", "error");
      return;
    }

    setCouponLoading(true);
    setCouponResult(null);

    const url = `/api/v1/coupon/find?code=${couponCode.trim().toUpperCase()}`;
    const allData = await getDataWithToken(url);

    if (allData?.status === 401) {
      logout();
      return;
    }

    if (allData.status >= 200 && allData.status < 300) {
      const found = allData?.data?.data;
      if (found && found._id) {
        setCouponResult(found);
      } else {
        handleSnakbarOpen("No coupon found", "error");
      }
    } else {
      handleSnakbarOpen(
        allData?.data?.message || "Failed to search coupon",
        "error"
      );
    }

    setCouponLoading(false);
  };

  const applyCoupon = () => {
    if (!couponResult) return;

    const discount = calculateCouponDiscount(couponResult);
    if (typeof set_discount_amount === "function") {
      set_discount_amount(discount);
    }
    setSelectedCoupon(couponResult);
    handleSnakbarOpen("Coupon applied successfully", "success");
    setCouponDialog(false);
    resetDialogState();
  };

  const handleCancelCoupon = () => {
    setSelectedCoupon(null);
    if (typeof set_discount_amount === "function") {
      set_discount_amount(0);
    }
    resetDialogState();
    handleSnakbarOpen("Coupon removed successfully", "info");
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<SearchIcon />}
          onClick={() => setCouponDialog(true)}
          sx={{ whiteSpace: "nowrap" }}
        >
          Search Coupon
        </Button>
        {selectedCoupon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 1,
            }}
          >
            <Typography
              variant="body2"
              color="primary"
              sx={{ fontWeight: 500 }}
            >
              Applied Coupon: {selectedCoupon.code}
              {selectedCoupon.discount_type === "percentage"
                ? ` (${selectedCoupon.discount_value}%)`
                : ` (Fixed ${selectedCoupon.discount_value})`}
            </Typography>
            <Tooltip title="Cancel Coupon" arrow>
              <IconButton
                size="small"
                onClick={handleCancelCoupon}
                aria-label="cancel coupon"
                sx={{
                  p: "2px",
                  color: "error.main",
                  "&:hover": {
                    bgcolor: "rgba(211, 47, 47, 0.08)",
                  },
                }}
              >
                <CancelIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Dialog
        open={couponDialog}
        onClose={handleCouponDialogClose}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            color: "#0F1624",
            position: "relative",
            px: 2,
            borderBottom: "1px solid #EAECF1",
          }}
        >
          Search Coupon
          <IconButton
            sx={{ position: "absolute", right: 0, top: 0 }}
            onClick={() => {
              setCouponDialog(false);
              resetDialogState();
            }}
          >
            <svg
              width="46"
              height="44"
              viewBox="0 0 46 44"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M29 16L17 28M17 16L29 28"
                stroke="#656E81"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            maxWidth: "420px",
            minWidth: "420px",
            px: 2,
            borderBottom: "1px solid #EAECF1",
            my: 1,
          }}
        >
          <Typography
            variant="medium"
            color="text.main"
            gutterBottom
            sx={{ fontWeight: 500 }}
          >
            Coupon Code
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Enter coupon code"
              variant="outlined"
              sx={{ ...customeTextFeild }}
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  searchCoupon();
                }
              }}
            />
            <Button
              variant="contained"
              disableElevation
              onClick={searchCoupon}
              disabled={couponLoading}
              sx={{ minWidth: "100px" }}
            >
              <PulseLoader
                color={"#4B46E5"}
                loading={couponLoading}
                size={8}
                speedMultiplier={0.5}
              />
              {!couponLoading && "Search"}
            </Button>
          </Box>

          {couponResult && (
            <Box
              sx={{
                border: "1px solid #EAECF1",
                borderRadius: "8px",
                p: 2,
                background: "#F9FAFB",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {couponResult.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Code: {couponResult.code}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type:{" "}
                {couponResult.discount_type === "percentage"
                  ? "Percentage"
                  : "Fixed"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Value:{" "}
                {couponResult.discount_type === "percentage"
                  ? `${couponResult.discount_value}%`
                  : couponResult.discount_value}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 1 }}>
                Total Amount: {totalAmount}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                Discount Amount: {calculateCouponDiscount(couponResult)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCouponDialogClose}
            sx={{
              px: 2,
              py: 1.25,
              fontSize: "14px",
              fontWeight: 600,
              color: "#344054",
              border: "1px solid #D0D5DD",
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={!couponResult}
            onClick={applyCoupon}
            sx={{ px: 2, py: 1.25, fontSize: "14px", fontWeight: 600 }}
          >
            Apply Coupon
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SearchCouponDialog;
